/** Linear-interpolation quantile (type 7) of a pre-sorted ascending array. */
export function quantile(sorted: number[], p: number): number {
  if (sorted.length === 0) return Number.NaN;
  if (sorted.length === 1) return sorted[0];
  const idx = (sorted.length - 1) * p;
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sorted[lo];
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
}

export interface BoxStats {
  min: number;
  q1: number;
  median: number;
  q3: number;
  max: number;
}

/** Five-number summary for a box plot. */
export function boxStats(values: number[]): BoxStats | null {
  const v = values.filter((n) => Number.isFinite(n)).sort((a, b) => a - b);
  if (v.length === 0) return null;
  return {
    min: v[0],
    q1: quantile(v, 0.25),
    median: quantile(v, 0.5),
    q3: quantile(v, 0.75),
    max: v[v.length - 1],
  };
}

export interface Histogram {
  labels: string[];
  counts: number[];
  edges: number[];
}

/** Bin values into a histogram. Bin count defaults to √n clamped to [5, 20]. */
export function histogram(values: number[], binCount?: number): Histogram {
  const v = values.filter((n) => Number.isFinite(n));
  if (v.length === 0) return { labels: [], counts: [], edges: [] };

  const bins = Math.max(
    1,
    binCount ?? Math.min(20, Math.max(5, Math.ceil(Math.sqrt(v.length)))),
  );
  const min = Math.min(...v);
  const max = Math.max(...v);
  if (min === max) {
    return { labels: [String(min)], counts: [v.length], edges: [min, max] };
  }

  const width = (max - min) / bins;
  const counts = new Array(bins).fill(0);
  for (const n of v) {
    let b = Math.floor((n - min) / width);
    if (b >= bins) b = bins - 1; // include the max in the last bin
    if (b < 0) b = 0;
    counts[b]++;
  }
  const edges = Array.from({ length: bins + 1 }, (_, i) => min + i * width);
  const round = (x: number) => Math.round(x * 100) / 100;
  const labels = Array.from(
    { length: bins },
    (_, i) => `${round(edges[i])}–${round(edges[i + 1])}`,
  );
  return { labels, counts, edges };
}
